<template>
  <div v-if="topic" ref="commentFormContainer">
    <div v-if="canCommentOnThoughts(topic)">
      <div class="alert alert-info" v-if="infotext !== ''">{{ infotext }}</div>
      <div class="alert alert-danger" v-if="serverErr !== ''">{{ serverErr }}</div>
      <div id="wdw-discuss-thought-commentform">
        <Form @submit="onSubmitComment">
          <div id="wdw-comment-form" class="wdw-form">
            <form-control-textarea
              label="Ihr Kommentar"
              name="content"
              code="comment"
              parentId="wdw-discuss-thought-commentform"
              error="Kommentar kann nicht leer sein"
              :defaultvalue="defaultContent"
              :required="true"
              :rows="5"
              :textLimitConfig="{
                maxCount: project.comment_maxlength,
                cLimitText: 'Maximale Zeichenanzahl erreicht',
              }"
            />
            <form-control-file
              v-if="toBool(topic.allow_comment_attachments)"
              name="attachment-file"
              code="attachment-file"
              :help="maxAttachmentSizeHint"
              label="Hier können Sie ein Bild hochladen"
              prefixId="wdw-discuss-thought-commentform"
              accepts="image/*"
              @validate="onValidatedFormField"
            />
            <div class="row" v-if="toBool(topic.allow_comment_attachments) && toBool(topic.allow_attaching_urls)">
              <form-control-input
                name="attach_link_url"
                code="attach_link_url"
                type="link"
                help="Der Link muss mit http:// | https:// beginnen"
                placeholder="https://"
                label="Hier können Sie einen Link anhängen"
                prefixId="wdw-discuss-thought-commentform"
              />
              <form-control-input
                name="attach_link_title"
                code="attach_link_title"
                label="Titel des Links"
                prefixId="wdw-discuss-thought-commentform"
              />
            </div>
            <form-control-checkbox
              v-if="isAdmin"
              label="Kommentar hervorheben"
              name="highlight"
              code="highlight"
              parentId="wdw-discuss-thought-commentform"
              :defaultvalue="defaultHighlighted"
            />

            <button type="submit" name="submit" class="btn btn-primary btn-form-submit" :disabled="loaderSubmitComment">
              Speichern
            </button>
            &nbsp;
            <button type="button" class="ml-2 btn btn-default btn-form-cancel" @click="onCancelPressed()">
              Abbrechen
            </button>
            <vue-loading v-if="loaderSubmitComment" />
          </div>
        </Form>
      </div>
    </div>
    <div v-else-if="canCommentAfterLogin(topic)" class="alert alert-info">
      Sie müssen sich erst einloggen um einen Kommentar abzuschicken
    </div>
  </div>
</template>
<script>
import { computed, onMounted, nextTick } from '@vue/runtime-core';
import { ref, toRefs, reactive } from '@vue/reactivity';

import { projectStore } from '@/store/project-store';
import { userStore } from '@/store/user-store';

import { Form } from 'vee-validate';
import FormControlTextarea from '@/components/general/FormControl/TextArea.vue';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox.vue';
import FormControlFile from '@/components/general/FormControl/File.vue';
import FormControlInput from '@/components/general/FormControl/Input.vue';
import VueLoading from '@/components/general/Loading.vue';

import useToggler from '@/composables/general/useToggler';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useCommentEvents from '@/composables/comment/useCommentEvents';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useConverter from '@/composables/general/useConverter';

import thoughtRepository from '@/api/thoughtRepository';
import commentRepository from '@/api/commentRepository';

// TODO
// [] -> get topic using topicid

export default {
  name: 'CommentForm',

  props: {
    thoughtid: {
      type: Number,
      required: true,
    },
    topicid: {
      required: true,
      type: Number,
    },
    comment: {
      type: Object,
      required: false,
    },
    withvote: {
      type: Number,
      required: false,
      default: 0,
      // 0 means that the vote should not be sent
    },
    infotext: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['comment-updated', 'comment-form-close', 'comment-vote-sent'],

  components: {
    Form,
    VueLoading,
    FormControlTextarea,
    FormControlCheckbox,
    FormControlFile,
    FormControlInput,
  },

  setup(props, context) {
    const { thoughtid, comment, withvote } = toRefs(props);
    const { emitCommentUpdated, emitCommentFormClose, emitCommentVoteSent } = useCommentEvents(context);
    const { canCommentOnThoughts, canCommentAfterLogin, canEditComment } = useTopicConfig();
    const { toggleValue: loaderSubmitComment, toggle: toggleLoaderSubmitComment } = useToggler(false);
    const { l } = useLocalizeMessages();
    const { toBool, bytesToHumanReadable } = useConverter();
    const commentFormContainer = ref(HTMLElement | null);

    const serverErr = ref('');
    const uploadedFiles = {};
    // Computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isAdmin = computed(() => {
      return userStore.isAdmin();
    });
    const topic = computed(() => {
      return projectStore.getFetchedTopic(props.topicid);
    });
    const defaultContent = computed(() => {
      if (comment && comment.value) {
        return comment.value.content;
      }
      return '';
    });
    const defaultHighlighted = computed(() => {
      if (comment && comment.value && comment.value.highlight) {
        return true;
      }
      return false;
    });
    const isInEditMode = computed(() => {
      return comment && comment.value && comment.value.commentid;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const maxAttachmentSizeHint = computed(() => {
      if (project.value && project.value.discuss && project.value.discuss.attachment_max_size) {
        return (
          'Die maximal zulässige Dateigröße beträgt ' + bytesToHumanReadable(project.value.discuss.attachment_max_size)
        );
      }
      return '';
    });
    // Methods
    const showError = error => {
      const errMsg = error.response.data.message;
      serverErr.value = l(errMsg);
    };

    const onSubmitComment = (form, actions) => {
      serverErr.value = '';
      toggleLoaderSubmitComment();
      const files = new Map();
      for (const [key, value] of Object.entries(uploadedFiles)) {
        files.set(key, value.value);
      }
      if (withvote.value == 0) {
        if (props.comment) {
          commentRepository
            .update(thoughtid.value, comment.value.commentid, form, files)
            .then(resp => {
              actions.resetForm();
              toggleLoaderSubmitComment();
              emitCommentUpdated(resp);
            })
            .catch(err => {
              showError(err);
              toggleLoaderSubmitComment();
            });
        } else {
          commentRepository
            .create(thoughtid.value, form, files)
            .then(resp => {
              actions.resetForm();
              toggleLoaderSubmitComment();
              emitCommentUpdated(resp);
            })
            .catch(err => {
              showError(err);
              toggleLoaderSubmitComment();
            });
        }
      } else {
        const params = {
          vote: withvote.value,
          highlight: form.highlight ? 1 : 0,
          comment: form.content,
          commmentid: props.comment ? comment.value.commentid : 0,
        };
        // Comment with vote
        thoughtRepository
          .postVoteComment(thoughtid.value, params, files)
          .then(resp => {
            actions.resetForm();
            toggleLoaderSubmitComment();
            // Success? refresh detail thought
            emitCommentUpdated(resp.comment);
            // Vote done
            emitCommentVoteSent(resp.support);
          })
          .catch(err => {
            // Error sending vote
            showError(err);
            toggleLoaderSubmitComment();
          });
      }
    };
    const onValidatedFormField = fieldValue => {
      if (fieldValue.type === 'file' && fieldValue.validated) {
        uploadedFiles[fieldValue.name] = fieldValue;
      }
    };
    const onCancelPressed = () => {
      emitCommentFormClose();
    };

    onMounted(() => {
      // make sure Comment-Form is scrolled into view when it's opened
      if (commentFormContainer.value) {
        nextTick(function () {
          commentFormContainer.value.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
          // get first input element inside target (input inside form-control-input.vue) to focues
          commentFormContainer.value.getElementsByTagName('textarea')[0].focus();
        });
      }
    });

    return {
      // Methods
      canCommentOnThoughts,
      canCommentAfterLogin,
      toggleLoaderSubmitComment,
      onValidatedFormField,
      onSubmitComment,
      onCancelPressed,
      toBool,
      // Computed
      project,
      topic,
      user,
      isAdmin,
      defaultContent,
      defaultHighlighted,
      isInEditMode,
      maxAttachmentSizeHint,
      // VARS
      loaderSubmitComment,
      serverErr,
      commentFormContainer,
    };
  },
};
</script>
<style scoped>
#wdw-discuss-thought-commentform {
  margin-bottom: 10px;
}
</style>
